import { DatePipe, NgClass } from '@angular/common'
import { ChangeDetectorRef, Component, Input, OnInit, computed, inject, signal } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faBuildingFlag, faCalendarAlt, faEdit, faSave, faTimes, faUser, faUsers } from '@fortawesome/free-solid-svg-icons'
import { QuillEditorComponent, QuillViewComponent } from 'ngx-quill'

import { BaseComponent, BusinessPlanSection, LEADERSHIP_L10_MEETING_ID, Rock } from '../../shared'
import { getInitials } from '../../shared/shared-functions'
import { BusinessPlanDashboardComponent } from './business-plan-dashboard.component'

@Component({
    selector: 'app-business-plan-summary',
    templateUrl: './business-plan-summary.component.html',
    styleUrls: ['./business-plan-summary.component.css'],
    standalone: true,
    imports: [FaIconComponent, QuillViewComponent, QuillEditorComponent, FormsModule, NgClass, DatePipe, BusinessPlanDashboardComponent],
})
export class BusinessPlanSummaryComponent extends BaseComponent implements OnInit {
    faEdit = faEdit
    faSave = faSave
    faTimes = faTimes
    faCalendarAlt = faCalendarAlt
    faUsers = faUsers
    faBuildingFlag = faBuildingFlag
    faUser = faUser

    private cdr = inject(ChangeDetectorRef)

    @Input() sections: BusinessPlanSection[] = []
    @Input() canEdit = false

    editingSections = signal<Record<string, boolean>>({})
    editedContent = signal<Record<string, string>>({})

    // ROCK data
    quarterlyRocks = signal<Rock[]>([])
    oneYearGoalRocks = signal<Rock[]>([])
    isLoadingRocks = signal(false)

    quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
            ['link', 'image', 'video'],
        ],
    }

    ngOnInit() {
        this.loadQuarterlyRocks()
    }

    isEditing(section: BusinessPlanSection) {
        return computed(() => {
            const editing = this.editingSections()
            return Object.prototype.hasOwnProperty.call(editing, section.id) && editing[section.id]
        })
    }

    startEditing(section: BusinessPlanSection) {
        // Prevent multiple simultaneous edits
        if (this.editingSections()[section.id]) {
            return
        }

        // Use setTimeout to ensure the DOM is ready
        setTimeout(() => {
            this.editingSections.update(editing => ({
                ...editing,
                [section.id]: true,
            }))

            const content = section.content || ''
            this.editedContent.update(editedContent => ({
                ...editedContent,
                [section.id]: content,
            }))
        }, 0)
    }

    cancelEditing(section: BusinessPlanSection) {
        this.editingSections.update(editing => {
            const newEditing = { ...editing }
            delete newEditing[section.id]
            return newEditing
        })
        this.editedContent.update(content => {
            const newContent = { ...content }
            delete newContent[section.id]
            return newContent
        })
    }

    saveSection(section: BusinessPlanSection) {
        const content = this.editedContent()[section.id]
        section.content = content

        this.services.data.businessPlanSections.edit(section).subscribe({
            next: () => {
                this.editingSections.update(editing => {
                    const newEditing = { ...editing }
                    delete newEditing[section.id]
                    return newEditing
                })
                this.editedContent.update(content => {
                    const newContent = { ...content }
                    delete newContent[section.id]
                    return newContent
                })
                this.services.notification.success('Section updated successfully')
            },
            error: () => {
                this.services.notification.error('Failed to save section changes')
            },
        })
    }

    onContentChange(section: BusinessPlanSection, content: string) {
        this.editedContent.update(editedContent => ({
            ...editedContent,
            [section.id]: content,
        }))
    }

    onEditorCreated() {
        // Manually trigger change detection to ensure the editor is properly rendered
        this.cdr.detectChanges()

        // Add a small delay to ensure the editor is fully rendered
        setTimeout(() => {
            this.cdr.detectChanges()
        }, 100)
    }

    loadQuarterlyRocks() {
        this.isLoadingRocks.set(true)
        const params = new HttpParams()
            .set('meetingId', LEADERSHIP_L10_MEETING_ID)
            .set('showArchived', 'false')
        this.cancelableRequest(
            'LoadQuarterlyRocks',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.users.queryAllByAction('GetForLookup'),
                    this.services.data.rocks.queryAllByAction('GetByMeetingId', params),
                )
                .subscribe({
                    next: result => {
                        let filteredRocks = result[1] as Rock[]

                        filteredRocks.forEach(rock => {
                            if (rock.doneDate !== null) {
                                rock.status = 2
                                rock.dueDate = rock.doneDate
                            }
                        })

                        // Add the userName to the rock using the user repository
                        filteredRocks.forEach(rock => {
                            rock.userName = this.services.data.users.items.find(user => user.id === rock.userId)?.displayName || ''
                        })

                        filteredRocks = filteredRocks.orderBy(rock => rock.dueDate)
                        filteredRocks = filteredRocks.orderByDescending(rock => rock.type)

                        // Group the rocks by name and then add the userNames to the resulting rocks
                        const groupedRocks = filteredRocks.toObjectGroups(
                            rock => rock.name,
                            rock => rock,
                        )

                        const quarterlyRocks: Rock[] = []
                        const oneYearGoalRocks: Rock[] = []
                        Object.entries(groupedRocks).forEach(([_name, rocks]) => {
                            const rock = rocks[0]
                            rock.userNames = rocks.map(groupedRock => groupedRock.userName)
                            if (rocks.some(groupedRock => groupedRock.includeInOneYearGoals)) {
                                oneYearGoalRocks.push(rock)
                            } else {
                                quarterlyRocks.push(rock)
                            }
                        })

                        this.quarterlyRocks.set(quarterlyRocks)
                        this.oneYearGoalRocks.set(oneYearGoalRocks.orderBy(rock => rock.name))
                        this.isLoadingRocks.set(false)
                    },
                    error: () => {
                        this.isLoadingRocks.set(false)
                        this.services.notification.error('Failed to load quarterly goals')
                    },
                }),
        )
    }

    hasQuarterlyGoalsSection() {
        return computed(() => {
            return this.sections.some(section => section.heading?.toLowerCase().includes('quarterly goals'))
        })
    }

    isOneYearGoalsSection(section: BusinessPlanSection) {
        return section.heading?.toLowerCase().includes('1-year goals')
    }

    getRockBulletLabel(rock: Rock): string {
        const initials = (rock.userNames || [])
            .map(name => getInitials(name))
            .filter(value => !!value)
            .join(', ')
        return initials ? `${rock.name} (${initials})` : rock.name
    }

    getStatusText(status: number): string {
        switch (status) {
            case 0:
                return 'Off Track'
            case 1:
                return 'On Track'
            case 2:
                return 'Done'
            case 3:
                return 'Cancelled'
            default:
                return 'Unknown'
        }
    }

    getStatusClass(status: number): string {
        switch (status) {
            case 0:
                return 'status-off-track'
            case 1:
                return 'status-on-track'
            case 2:
                return 'status-done'
            case 3:
                return 'status-cancelled'
            default:
                return 'status-unknown'
        }
    }

    trackByRockId(index: number, rock: Rock): string {
        return rock.id
    }

    getRockIcon(type: number) {
        switch (type) {
            case 0:
                return this.faUser
            case 1:
                return this.faBuildingFlag
            default:
                return this.faUser
        }
    }
}
